import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useSidebarCounts } from '../../services/dataService';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Badge,
  useTheme,
  alpha,
  Chip,
  Skeleton,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard,
  People,
  Flight,
  CardGiftcard,
  AccountBalance,
  Business,
  CalendarToday,
  Description,
  Assessment,
  Settings,
  Notifications,
  Search,
  MenuOpen,
  AccountCircle,
  Flight as FlightIcon,
  Menu as MenuIcon,
  Logout,
  ExitToApp
} from '@mui/icons-material';

// Responsive drawer width
const getDrawerWidth = (isMobile) => isMobile ? 280 : 240;

const menuItems = [
  { 
    text: 'Tableau de Bord', 
    icon: <Dashboard />, 
    path: '/tableau-de-bord',
    badge: null
  },
  { 
    text: 'Clients', 
    icon: <People />, 
    path: '/clients',
    countKey: 'clients'
  },
  { 
    text: 'Réservations', 
    icon: <Flight />, 
    path: '/reservations',
    countKey: 'reservations'
  },
  { 
    text: 'Forfaits', 
    icon: <CardGiftcard />, 
    path: '/forfaits',
    countKey: 'forfaits'
  },
  { 
    text: 'Finances', 
    icon: <AccountBalance />, 
    path: '/finances',
    badge: null
  },
  { 
    text: 'Fournisseurs', 
    icon: <Business />, 
    path: '/fournisseurs',
    badge: null
  },
  { 
    text: 'Calendrier', 
    icon: <CalendarToday />, 
    path: '/calendrier',
    badge: null
  },
  { 
    text: 'Documents', 
    icon: <Description />, 
    path: '/documents',
    countKey: 'documents'
  },
  { 
    text: 'Rapports', 
    icon: <Assessment />, 
    path: '/rapports',
    badge: null
  },
  { 
    text: 'Paramètres', 
    icon: <Settings />, 
    path: '/parametres',
    badge: null
  },
];

const Layout = () => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { counts, loading: countsLoading } = useSidebarCounts();
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const drawerWidth = getDrawerWidth(isMobile);

  // Close drawer by default on mobile
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.text : settings.appName || 'TravelIT';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Modern AppBar with Glassmorphism */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 1, sm: 2, md: 3 },
          minHeight: { xs: 56, sm: 64 }
        }}>
          {/* Left side - Logo and current page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <IconButton
              color="inherit"
              onClick={toggleDrawer}
              sx={{ 
                mr: { xs: 0.5, sm: 1 },
                color: 'primary.main',
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(99, 102, 241, 0.2)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Logo - Using the downloaded image */}
            <Avatar
              src="/images/logo.png"
              sx={{ 
                width: { xs: 42, sm: 52, md: 56 }, 
                height: { xs: 42, sm: 52, md: 56 },
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              // Hide on very small screens
              display: { xs: 'none', sm: 'flex' } 
            }}>
              <Typography variant="body2" color="text.secondary">
                /
              </Typography>
              <Typography variant="h6" color="text.primary" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' } 
              }}>
                {getCurrentPageTitle()}
              </Typography>
            </Box>
          </Box>

          {/* Right side - Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton 
              sx={{ 
                borderRadius: 2,
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <Search />
            </IconButton>
            <IconButton 
              sx={{ 
                borderRadius: 2
              }}
            >
              <Badge badgeContent={4} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            <Box 
              onClick={() => {
                // Handle logout
                localStorage.removeItem('isAuthenticated');
                localStorage.removeItem('user');
                navigate('/login');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
            >
              <Avatar 
                src={localStorage.getItem('user') ? 
                  JSON.parse(localStorage.getItem('user')).photoURL || "/images/avatar.png" : 
                  "/images/avatar.png"
                }
                sx={{ 
                  width: { xs: 32, sm: 36 }, 
                  height: { xs: 32, sm: 36 }
                }}
              />
              <Box sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {localStorage.getItem('user') ? 
                    JSON.parse(localStorage.getItem('user')).name || 'Ahmed' : 
                    'Ahmed'
                  }
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                  Déconnexion
                </Typography>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Enhanced Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "temporary"}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '8px 0 32px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        
        {/* User Profile Section - Compact */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Avatar 
            src={settings.agencyLogo || "/images/logo.png"}
            sx={{ 
              width: { xs: 50, sm: 40 }, 
              height: { xs: 50, sm: 40 }, 
              mx: 'auto', 
              mb: 1
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {settings.agencyName || 'Agence de Voyage'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {settings.businessLicense || 'Licence en cours'}
          </Typography>
          <Chip 
            label="En ligne" 
            color="success" 
            variant="outlined" 
            size="small"
            sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
          />
        </Box>

        <Divider sx={{ mx: 2 }} />

        {/* Navigation Menu */}
        <List sx={{ px: 1, pt: 2 }}>
            {menuItems.map((item) => {
            // Get count for this menu item if available
            let badgeContent = null;
            
            if (item.countKey && !countsLoading && counts) {
              badgeContent = counts[item.countKey] || 0;
            } else if (item.badge !== null) {
              badgeContent = item.badge;
            }
            
              const isSelected = location.pathname === item.path;
            
              return (
                <ListItem 
                key={item.text}
                  button 
                selected={isSelected}
                  onClick={() => handleMenuClick(item.path)}
                  sx={{
                    mb: 0.5,
                    py: isMobile ? 1.5 : 1,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                      '& .MuiChip-root': {
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                        color: 'white',
                      }
                    }
                  }}
                >
                <ListItemIcon sx={{ 
                  minWidth: { xs: 45, sm: 40 }, 
                  color: isSelected ? 'inherit' : 'primary.main',
                  '& .MuiSvgIcon-root': {
                    fontSize: isMobile ? '1.5rem' : '1.25rem'
                  }
                }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{ 
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: isMobile ? '1rem' : '0.95rem'
                    }}
                  />
                
                {badgeContent !== null && (
                  countsLoading ? (
                    <Skeleton variant="circular" width={24} height={24} />
                  ) : (
                    <Chip 
                      label={badgeContent} 
                      size="small"
                      sx={{ 
                        height: 24, 
                        minWidth: 24, 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: isSelected ? 
                          alpha(theme.palette.common.white, 0.2) : 
                          alpha(theme.palette.primary.main, 0.1),
                        color: isSelected ? 'white' : 'primary.main'
                      }}
                    />
                  )
                  )}
                </ListItem>
              );
            })}
          </List>
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Logout Button */}
        <Box sx={{ px: 2, mb: 2 }}>
          <ListItem 
            button 
            onClick={() => {
              localStorage.removeItem('isAuthenticated');
              localStorage.removeItem('user');
              navigate('/login');
            }}
            sx={{ 
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: 36,
              color: theme.palette.error.main
            }}>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText 
              primary="Déconnexion" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 500,
                color: theme.palette.error.main
              }}
            />
          </ListItem>
        </Box>
        
        {/* Version Info */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Version {settings?.appVersion || '1.0.0'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {settings?.appCopyright || '© 2024 TravelIT'}
          </Typography>
        </Box>
      </Drawer>
      
      {/* Main Content */}
      <Box component="main" sx={{ 
        flexGrow: 1, 
        p: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ 
          p: { xs: 1, sm: 2, md: 3 },
          overflowX: 'hidden'
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout; 